import React, { createContext, useContext, useState, useEffect } from "react";

// Create AuthContext
const AuthContext = createContext();

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentShop, setCurrentShop] = useState(null);

  // Get dashboard path based on user role
  const getDashboardPathByRole = (role) => {
    if (role === 'super_admin') {
      return '/DashboardSuperAdmin';
    }

    // POS roles
    if (role === 'admin_pos' || role === 'manager_pos' || role === 'user_pos' || role === 'viewer_pos') {
      return '/DashboardPOS';
    }

    // PO roles - Qadar (ST FOODS COMPANY QUETTA)
    if (role === 'admin_po' || role === 'manager_po' || role === 'user_po' || role === 'viewer_po') {
      return '/DashboardPO';
    }

    // Fabrics roles
    if (role === 'admin_fabrics' || role === 'manager_fabrics' || role === 'user_fabrics' || role === 'viewer_fabrics') {
      return '/DashboardFabrics';
    }

    // Mechanics Thermal Printer roles
    if (role === 'admin_mechanicsth' || role === 'manager_mechanicsth' || role === 'user_mechanicsth' || role === 'viewer_mechanicsth') {
      return '/DashboardMechanicsTh';
    }

    // Mechanics Large Printer roles
    if (role === 'admin_mechanicsap' || role === 'manager_mechanicsap' || role === 'user_mechanicsap' || role === 'viewer_mechanicsap') {
      return '/DashboardMechanicsAp';
    }

    // Hardware Thermal Printer roles
    if (role === 'admin_hardwareth' || role === 'manager_hardwareth' || role === 'user_hardwareth' || role === 'viewer_hardwareth') {
      return '/DashboardHardwareTh';
    }

    // Hardware Large Printer roles
    if (role === 'admin_hardwareap' || role === 'manager_hardwareap' || role === 'user_hardwareap' || role === 'viewer_hardwareap') {
      return '/DashboardHardwareAp';
    }

    // Autos Thermal Printer roles
    if (role === 'admin_autosth' || role === 'manager_autosth' || role === 'user_autosth' || role === 'viewer_autosth') {
      return '/DashboardAutosTh';
    }

    // Autos Large Printer roles
    if (role === 'admin_autosap' || role === 'manager_autosap' || role === 'user_autosap' || role === 'viewer_autosap') {
      return '/DashboardAutosAp';
    }

    // Pharmacy role
    if (role === 'admin_pharmacy' || role === 'manager_pharmacy' || role === 'user_pharmacy' || role === 'viewer_pharmacys') {
      return '/DashboardAutosAp';
    }

    // Default to legacy dashboard
    return '/Dashboard';
  };

  // Fetch customer's shop from server
  const fetchCustomerShop = async (userId) => {
    try {
      console.log("Fetching customer shop for user ID:", userId);
      const response = await fetch('https://ourzestapp.online/oz_backend/customers.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_customer_shop',
          user_id: userId
        })
      });
      
      const data = await response.json();
      console.log("Customer shop response:", data);
      
      if (data.status === 'success' && data.shop_id) {
        return {
          shop_id: data.shop_id,
          shop_name: data.shop_name || `Shop #${data.shop_id}`,
          shop_code: data.shop_code
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching customer shop:", error);
      return null;
    }
  };

  // Fetch customer details by user_id
  const fetchCustomerDetails = async (userId) => {
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/customers.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_customer_by_user_id',
          user_id: userId
        })
      });
      
      const data = await response.json();
      if (data.status === 'success' && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching customer details:", error);
      return null;
    }
  };

  // Initialize user from localStorage with security validation
  useEffect(() => {
    const initializeUser = async () => {
      try {
        console.log("Initializing user from localStorage");
        const savedUser = localStorage.getItem("user");
        const savedRole = localStorage.getItem("user_role");
        const authTimestamp = localStorage.getItem("auth_timestamp");
        const savedShop = localStorage.getItem("current_shop");
        const savedTheme = localStorage.getItem("ourzest_theme");
        const savedLanguage = localStorage.getItem("ourzest_language");
        
        console.log("Saved user:", savedUser);
        console.log("Saved role:", savedRole);
        console.log("Saved shop:", savedShop);
        
        // Validate timestamp
        if (authTimestamp) {
          const timeDiff = Date.now() - parseInt(authTimestamp);
          if (timeDiff > 24 * 60 * 60 * 1000) {
            // Session expired
            console.log("Session expired");
            localStorage.clear();
            setUser(null);
            setCurrentShop(null);
            setLoading(false);
            return;
          }
        }
        
        if (savedUser && savedRole) {
          const userData = JSON.parse(savedUser);
          console.log("Parsed user data:", userData);
          
          // Validate role integrity
          if (userData.role !== savedRole) {
            // Role mismatch - potential tampering
            console.log("Role mismatch");
            localStorage.clear();
            setUser(null);
            setCurrentShop(null);
          } else {
            // Always use role from separate storage
            userData.role = savedRole;
            
            // Apply theme and language
            const themeToApply = savedTheme || userData.themeColor || '#6366f1';
            userData.themeColor = themeToApply;
            
            const languageToApply = savedLanguage || userData.language || 'en';
            userData.language = languageToApply;
            
            // Apply theme immediately
            document.documentElement.style.setProperty('--primary-color', themeToApply);
            
            // Apply language direction immediately
            const isRTL = languageToApply === 'ur' || languageToApply === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            
            setUser(userData);
            
            // Try to load current shop
            let shopToSet = null;
            
            // First try saved shop
            if (savedShop) {
              try {
                shopToSet = JSON.parse(savedShop);
                console.log("Using saved shop:", shopToSet);
              } catch (e) {
                console.error("Error parsing saved shop:", e);
              }
            }
            
            // If no saved shop or invalid, try user data
            if (!shopToSet && userData.shop_id) {
              shopToSet = {
                shop_id: userData.shop_id,
                shop_name: userData.shop_name || `Shop #${userData.shop_id}`,
                shop_code: userData.shop_code
              };
              console.log("Using shop from user data:", shopToSet);
            }
            
            // If still no shop and user is a customer, try to fetch from server
            if (!shopToSet && userData.role === 'customer' && userData.id) {
              console.log("No shop found, fetching from server for customer:", userData.id);
              
              // First try to get customer details
              const customerDetails = await fetchCustomerDetails(userData.id);
              if (customerDetails && customerDetails.shop_id) {
                shopToSet = {
                  shop_id: customerDetails.shop_id,
                  shop_name: customerDetails.shop_name || `Shop #${customerDetails.shop_id}`,
                  shop_code: customerDetails.shop_code
                };
                
                // Update user data with shop_id
                userData.shop_id = customerDetails.shop_id;
                userData.shop_name = customerDetails.shop_name;
                userData.shop_code = customerDetails.shop_code;
                userData.customer_id = customerDetails.id;
                
                // Save updated user data
                localStorage.setItem("user", JSON.stringify(userData));
                setUser(userData);
                
                console.log("Updated user with shop data:", userData);
              } else {
                // Fallback to customer shop endpoint
                shopToSet = await fetchCustomerShop(userData.id);
                if (shopToSet) {
                  console.log("Fetched shop from server:", shopToSet);
                  // Update user data with shop_id
                  userData.shop_id = shopToSet.shop_id;
                  userData.shop_name = shopToSet.shop_name;
                  userData.shop_code = shopToSet.shop_code;
                  localStorage.setItem("user", JSON.stringify(userData));
                  setUser(userData);
                }
              }
            }
            
            // For admin users, try default_shop_id
            if (!shopToSet && userData.default_shop_id) {
              shopToSet = {
                shop_id: userData.default_shop_id,
                shop_name: userData.shop_name || `Shop #${userData.default_shop_id}`,
                shop_code: userData.shop_code
              };
              console.log("Using default shop:", shopToSet);
            }
            
            // For super admin, set default shop if none exists
            if (!shopToSet && userData.role === 'super_admin') {
              shopToSet = {
                shop_id: 1,
                shop_name: "Main Shop",
                shop_code: "SHOP001"
              };
              console.log("Setting default shop for super admin:", shopToSet);
            }
            
            // Set the shop if found
            if (shopToSet) {
              setCurrentShop(shopToSet);
              localStorage.setItem("current_shop", JSON.stringify(shopToSet));
            } else {
              console.log("No shop found for user");
              setCurrentShop(null);
            }
          }
        } else {
          console.log("No saved user found");
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.clear();
        setUser(null);
        setCurrentShop(null);
      } finally {
        setLoading(false);
        console.log("Auth initialization complete. User:", user, "CurrentShop:", currentShop);
      }
    };

    initializeUser();
  }, []);

  // Reset to default shop
  const resetToDefaultShop = (userData) => {
    if (userData && userData.default_shop_id) {
      setCurrentShop({
        shop_id: userData.default_shop_id,
        shop_name: userData.shop_name || `Shop #${userData.default_shop_id}`,
        shop_code: userData.shop_code
      });
      localStorage.setItem("current_shop", JSON.stringify({
        shop_id: userData.default_shop_id,
        shop_name: userData.shop_name || `Shop #${userData.default_shop_id}`,
        shop_code: userData.shop_code
      }));
    } else if (userData && userData.role === 'super_admin') {
      // Default shop for super admin
      setCurrentShop({
        shop_id: 1,
        shop_name: "Main Shop",
        shop_code: "SHOP001"
      });
      localStorage.setItem("current_shop", JSON.stringify({
        shop_id: 1,
        shop_name: "Main Shop",
        shop_code: "SHOP001"
      }));
    } else {
      setCurrentShop(null);
      localStorage.removeItem("current_shop");
    }
  };

 const saveUser = (userData) => {
    console.log("Saving user:", userData);
    
    // Get theme and language from userData or fallback to localStorage
    const themeColor = userData.themeColor || localStorage.getItem('ourzest_theme') || '#6366f1';
    const language = userData.language || localStorage.getItem('ourzest_language') || 'en';
    
    // Force update localStorage with these values
    localStorage.setItem('ourzest_theme', themeColor);
    localStorage.setItem('ourzest_language', language);
    
    // Save session_id if present
    if (userData.session_id) {
        localStorage.setItem('session_id', userData.session_id);
    }
    
    // Apply theme immediately
    document.documentElement.style.setProperty('--primary-color', themeColor);
    
    // Apply language direction immediately
    const isRTL = language === 'ur' || language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    
    // Create enhanced user data with theme/language
    const enhancedUserData = {
        ...userData,
        themeColor,
        language
    };
    
    // Save role separately for security
    if (enhancedUserData && enhancedUserData.role) {
        localStorage.setItem("user_role", enhancedUserData.role);
        localStorage.setItem("auth_timestamp", Date.now().toString());
    }
    
    setUser(enhancedUserData);
    localStorage.setItem("user", JSON.stringify(enhancedUserData));
    
    // For customers, we need to ensure shop_id is set
    if (enhancedUserData.role === 'customer') {
        // If we have shop_id in userData, use it
        if (userData.shop_id) {
            const shopData = {
                shop_id: userData.shop_id,
                shop_name: userData.shop_name || `Shop #${userData.shop_id}`,
                shop_code: userData.shop_code
            };
            console.log("Setting current shop for customer from userData:", shopData);
            setCurrentShop(shopData);
            localStorage.setItem("current_shop", JSON.stringify(shopData));
        }
    }
    // For super admin, set default shop
    else if (enhancedUserData.role === 'super_admin') {
        const shopData = {
            shop_id: userData.shop_id || 1,
            shop_name: userData.shop_name || "Main Shop",
            shop_code: userData.shop_code || "SHOP001"
        };
        console.log("Setting current shop for super admin:", shopData);
        setCurrentShop(shopData);
        localStorage.setItem("current_shop", JSON.stringify(shopData));
    }
    // For non-customer users with shop_id
    else if (userData.shop_id) {
        const shopData = {
            shop_id: userData.shop_id,
            shop_name: userData.shop_name || `Shop #${userData.shop_id}`,
            shop_code: userData.shop_code
        };
        console.log("Setting current shop from saveUser:", shopData);
        setCurrentShop(shopData);
        localStorage.setItem("current_shop", JSON.stringify(shopData));
    }
    // For admin/super_admin, only set if they have a default shop
    else if (userData.default_shop_id) {
        const shopData = {
            shop_id: userData.default_shop_id,
            shop_name: userData.shop_name || `Shop #${userData.default_shop_id}`,
            shop_code: userData.shop_code
        };
        console.log("Setting current shop from default:", shopData);
        setCurrentShop(shopData);
        localStorage.setItem("current_shop", JSON.stringify(shopData));
    }
};

  // Switch to a different shop - UPDATED VERSION with role switching
  const switchShop = async (shopId, shopName = null, shopCode = null) => {
    if (!user || !shopId) return false;

    // For super admin, handle shop switch locally
    if (user.role === 'super_admin') {
      const newShop = {
        shop_id: shopId,
        shop_name: shopName || `Shop #${shopId}`,
        shop_code: shopCode || `SHOP${shopId}`,
        shop_role: 'super_admin'
      };
      
      setCurrentShop(newShop);
      localStorage.setItem("current_shop", JSON.stringify(newShop));
      
      // Update user's current shop
      const updatedUser = {
        ...user,
        shop_id: shopId,
        shop_name: newShop.shop_name,
        shop_code: newShop.shop_code,
        current_shop_role: 'super_admin'
      };
      
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      return true;
    }

    try {
      // First get the shop-specific role
      let shopRole = null;
      
      if (user.shop_roles) {
        // Check if we have shop roles in user data
        const shopRoleData = user.shop_roles.find(r => r.shop_id == shopId);
        if (shopRoleData) {
          shopRole = shopRoleData.role;
        }
      }
      
      // If not found in user data, fetch from API
      if (!shopRole) {
        const roleResponse = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_user_shop_roles',
            user_id: user.id
          })
        });
        
        const roleData = await roleResponse.json();
        
        if (roleData.success && roleData.data) {
          const shopRoleData = roleData.data.find(r => r.shop_id == shopId);
          if (shopRoleData) {
            shopRole = shopRoleData.role;
          }
        }
      }
      
      // If still no specific role, use user's main role
      if (!shopRole) {
        shopRole = user.role;
      }
      
      // Update on server - using new endpoint that also updates role
      const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'switch_shop_with_role',
          user_id: user.id,
          shop_id: shopId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const newShop = {
          shop_id: shopId,
          shop_name: shopName || data.shop?.shop_name || `Shop #${shopId}`,
          shop_code: shopCode || data.shop?.shop_code,
          shop_role: shopRole
        };
        
        setCurrentShop(newShop);
        localStorage.setItem("current_shop", JSON.stringify(newShop));
        
        // Update user's current shop AND role in state
        const updatedUser = {
          ...user,
          shop_id: shopId,
          shop_name: newShop.shop_name,
          shop_code: newShop.shop_code,
          role: shopRole,  // Update role to shop-specific role
          current_shop_role: shopRole  // Also store separately
        };
        
        setUser(updatedUser);
        
        // Update localStorage with new role
        localStorage.setItem("user", JSON.stringify(updatedUser));
        localStorage.setItem("user_role", shopRole);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error switching shop:', error);
      return false;
    }
  };

  // Set default shop
  const setDefaultShop = async (shopId) => {
    if (!user || !shopId) return false;

    // For super admin, handle locally
    if (user.role === 'super_admin') {
      const updatedUser = {
        ...user,
        default_shop_id: shopId,
        shop_id: shopId
      };
      
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      // Update current shop if it's the current one
      if (currentShop?.shop_id === shopId) {
        const updatedShop = {
          ...currentShop,
          shop_id: shopId
        };
        setCurrentShop(updatedShop);
        localStorage.setItem("current_shop", JSON.stringify(updatedShop));
      }
      
      return true;
    }

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'set_default_shop',
          user_id: user.id,
          shop_id: shopId
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        // Get shop-specific role
        let shopRole = null;
        
        if (user.shop_roles) {
          const shopRoleData = user.shop_roles.find(r => r.shop_id == shopId);
          if (shopRoleData) {
            shopRole = shopRoleData.role;
          }
        }
        
        // If no specific role, use current role
        if (!shopRole) {
          shopRole = user.role;
        }
        
        // Update user in state
        const updatedUser = {
          ...user,
          default_shop_id: shopId,
          shop_id: shopId,
          shop_name: data.shop?.shop_name || user.shop_name,
          shop_code: data.shop?.shop_code || user.shop_code,
          role: shopRole,
          current_shop_role: shopRole
        };
        
        setUser(updatedUser);
        
        // Update localStorage
        localStorage.setItem("user", JSON.stringify(updatedUser));
        localStorage.setItem("user_role", shopRole);
        
        // Update current shop if it's the current one
        if (currentShop?.shop_id === shopId) {
          const updatedShop = {
            ...currentShop,
            shop_name: data.shop?.shop_name || currentShop.shop_name,
            shop_code: data.shop?.shop_code || currentShop.shop_code
          };
          setCurrentShop(updatedShop);
          localStorage.setItem("current_shop", JSON.stringify(updatedShop));
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error setting default shop:', error);
      return false;
    }
  };

  // Get user's assigned shops with roles
  const getUserShops = async () => {
    if (!user?.id) return [];

    // For super admin, return default shops list
    if (user.role === 'super_admin') {
      return [
        { shop_id: 1, shop_name: "Main Shop", role: "super_admin" },
        { shop_id: 2, shop_name: "Branch 1", role: "super_admin" },
        { shop_id: 3, shop_name: "Branch 2", role: "super_admin" }
      ];
    }

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get_user_shops',
          user_id: user.id
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        return data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching user shops:', error);
      return [];
    }
  };

  // Get user's current shop role
  const getCurrentShopRole = async () => {
    if (!user?.id) return null;

    // For super admin
    if (user.role === 'super_admin') {
      return 'super_admin';
    }

    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_current_shop_role&user_id=' + user.id, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return data.current_role;
      }
      return user.role;
    } catch (error) {
      console.error('Error fetching current shop role:', error);
      return user.role;
    }
  };

  // Refresh user data with shop roles
  const refreshUserData = async () => {
    if (!user?.id) return;

    // For super admin, no need to refresh from server
    if (user.role === 'super_admin') {
      console.log('Super admin, skipping refresh from server');
      return;
    }

    try {
      // Get updated user data with shop roles
      const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=list&search=&user_id=' + user.id, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        const updatedUser = data.data[0];
        
        // Get shop roles
        const roleResponse = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_user_shop_roles',
            user_id: user.id
          })
        });
        
        const roleData = await roleResponse.json();
        if (roleData.success) {
          updatedUser.shop_roles = roleData.data;
        }
        
        // Update state
        setUser(prev => ({ ...prev, ...updatedUser }));
        localStorage.setItem("user", JSON.stringify({ ...user, ...updatedUser }));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Get shops for switching (with role info)
  const getShopsForSwitch = async () => {
    // For super admin, return default shops
    if (user?.role === 'super_admin') {
      return {
        shops: [
          { shop_id: 1, shop_name: "Main Shop", role: "super_admin" },
          { shop_id: 2, shop_name: "Branch 1", role: "super_admin" },
          { shop_id: 3, shop_name: "Branch 2", role: "super_admin" }
        ],
        hasMultiple: true,
        currentShopId: currentShop?.shop_id || 1,
        shopCount: 3
      };
    }
    
    if (!user?.id) return { shops: [], hasMultiple: false };
    
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get_user_shop_roles',
          user_id: user.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        return {
          shops: data.data || [],
          hasMultiple: data.has_multiple_shops || false,
          currentShopId: data.current_shop_id,
          shopCount: data.shop_count || 0
        };
      }
      return { shops: [], hasMultiple: false };
    } catch (error) {
      console.error('Error fetching shops for switch:', error);
      return { shops: [], hasMultiple: false };
    }
  };

// Log logout to history
const logLogout = async (userId, sessionId) => {
    if (!userId) return;
    
    try {
        await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'log_logout',
                user_id: userId,
                session_id: sessionId
            })
        });
    } catch (error) {
        console.error('Error logging logout:', error);
    }
};
const logout = async () => {
    // Log logout before clearing session
    if (user?.id) {
        const sessionId = localStorage.getItem('session_id');
        await logLogout(user.id, sessionId);
    }
    
    // Keep theme and language preferences, only clear session data
    const savedTheme = localStorage.getItem('ourzest_theme');
    const savedLanguage = localStorage.getItem('ourzest_language');
    const savedUsername = localStorage.getItem('ourzest_username');
    const savedRemember = localStorage.getItem('ourzest_remember');
    
    // Clear session data only
    localStorage.removeItem("user");
    localStorage.removeItem("user_role");
    localStorage.removeItem("auth_timestamp");
    localStorage.removeItem("current_shop");
    localStorage.removeItem("session_id");
    
    // Restore theme and language preferences
    if (savedTheme) localStorage.setItem('ourzest_theme', savedTheme);
    if (savedLanguage) localStorage.setItem('ourzest_language', savedLanguage);
    if (savedUsername) localStorage.setItem('ourzest_username', savedUsername);
    if (savedRemember) localStorage.setItem('ourzest_remember', savedRemember);
    
    setUser(null);
    setCurrentShop(null);
};

  return (
    <AuthContext.Provider value={{ 
        user, 
        setUser: saveUser, 
        currentShop,
        switchShop,
        setDefaultShop,
        getUserShops,
        getShopsForSwitch,
        getCurrentShopRole,
        refreshUserData,
        resetToDefaultShop,
        getDashboardPathByRole,
        logout, 
        loading 
    }}>
        {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};